import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import { format } from 'date-fns';
import icons from 'style';
import translate from 'utils/fnTranslate';
import { TVSeriesDetailEpisode } from 'types/Asset';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { Lane } from 'components/Lane/Lane';
import { ArrowIcon } from 'components/Lane/Lane.css';
import { Picture } from 'components/Picture/Picture';
import { CardOverlays } from 'components/Cards/Card/parts/CardOverlays';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { getPlayerEpisodeContentMarker } from 'utils/fnContentMarker';
import { formatDate, getStartEndTimes } from 'utils/fnDate';
import { DropdownMenu, PLAYER_EPISODES_DROPDOWN_EVENT } from 'components/DropdownMenu/DropdownMenu';
import { DropdownTriggerButton } from 'components/DropdownMenu/DropdownMenu.css';
import { ControlGroup } from 'components/Player/PlayerWrapper';
import { usePlayer } from 'providers/player/PlayerContext';
import { SeasonNumber } from 'components/Pages/PageSeriesDetail/PageSeriesDetail.css';
import { usePlayerSeriesEpisodes } from 'providers/usePlayerSeriesEpisodes/PlayerSeriesEpisodesContext';
import { TIME_FORMAT_HH_MM } from 'utils/fnParser';
import {
    EpisodeAiringTime,
    EpisodeDescription,
    EpisodeInfo,
    EpisodeList,
    EpisodeListGradient,
    EpisodeListWrapper,
    EpisodeLockIcon,
    EpisodeRow,
    EpisodeSelectorContentWrapper,
    EpisodeSelectorMobileCloseButton,
    EpisodeSelectorMobileWrapper,
    EpisodeSelectorWrapper,
    EpisodeThumbnail,
    EpisodeTitle,
    EpisodesEmptyState,
    SeasonsLabel,
    SeasonsLaneWrapper,
    SeasonsRow,
} from './PlayerEpisodeSelector.css';

const SCROLL_EDGE_THRESHOLD = 2;

const getAiringTime = (episode: TVSeriesDetailEpisode) => {
    const listing = episode?.listings?.[0];

    if (!listing?.start || !listing?.duration) {
        return null;
    }

    const { startTime, endTime } = getStartEndTimes(listing.start, listing.duration);

    return {
        day: formatDate(startTime),
        time: `${format(startTime, TIME_FORMAT_HH_MM)} - ${format(endTime, TIME_FORMAT_HH_MM)}`,
    };
};

const getProgress = (episode: TVSeriesDetailEpisode) => {
    const duration = episode?.duration || episode?.listings?.[0]?.duration;

    if (!duration || !episode?.bookmark) {
        return 0;
    }

    return (episode.bookmark * 100) / duration;
};

const EpisodeSelectorContent: FC = () => {
    const { config } = useConfig();
    const { seasons, episodes, activeSeason, selectSeason, playingEpisodeId, getEpisodeState, playEpisode, openEpisodeDetails } =
        usePlayerSeriesEpisodes();

    const episodeListRef = useRef<HTMLDivElement>(null);
    const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
    const [isScrolledFromBottom, setIsScrolledFromBottom] = useState(false);

    const bookmarkThreshold = config?.app_config?.player_settings?.bookmark_threshold;

    const updateScrollEdges = () => {
        const list = episodeListRef.current;

        if (!list) {
            return;
        }

        setIsScrolledFromTop(list.scrollTop > SCROLL_EDGE_THRESHOLD);
        setIsScrolledFromBottom(list.scrollHeight - list.clientHeight - list.scrollTop > SCROLL_EDGE_THRESHOLD);
    };

    useEffect(() => {
        episodeListRef.current?.scrollTo({ top: 0 });
        updateScrollEdges();
    }, [episodes]);

    return (
        <EpisodeSelectorContentWrapper>
            {seasons.length > 0 && (
                <SeasonsRow>
                    <SeasonsLabel>{translate('NUMBER_SEASONS')}</SeasonsLabel>
                    <SeasonsLaneWrapper>
                        <Lane
                            arrowLeft={<ArrowIcon svg={icons.leftArrowIcon} />}
                            arrowRight={<ArrowIcon svg={icons.rightArrowIcon} />}
                            scrollable={true}
                            keepArrows={false}
                            showArrowsWhenActive={true}
                            withoutPadding={true}
                            stripeType="seasons"
                        >
                            {seasons.map((season) => (
                                <SeasonNumber
                                    data-test-id={'player-episodes-season'}
                                    key={`player-season-${season}`}
                                    type="button"
                                    selected={season === activeSeason}
                                    aria-pressed={season === activeSeason}
                                    aria-label={season > 0 ? `${translate('NUMBER_SEASON')} ${season}` : undefined}
                                    onClick={() => selectSeason(season)}
                                >
                                    {season > 0 ? season : translate('SEASON_SELECTOR_OTHER_EPISODES')}
                                </SeasonNumber>
                            ))}
                        </Lane>
                    </SeasonsLaneWrapper>
                </SeasonsRow>
            )}

            {episodes.length === 0 && <EpisodesEmptyState>{translate('EPG_NO_INFO')}</EpisodesEmptyState>}

            <EpisodeListWrapper>
                <EpisodeListGradient isBottom={false} isVisible={isScrolledFromTop} aria-hidden={true} />
                <EpisodeList ref={episodeListRef} onScroll={updateScrollEdges}>
                    {episodes.map((episode) => {
                        const isPlaying = !!playingEpisodeId && episode.id === playingEpisodeId;
                        const airingTime = getAiringTime(episode);
                        const progress = getProgress(episode);
                        const { isPlayable, requiresSubscription, isUpcoming } = getEpisodeState(episode);
                        const isSelectable = isPlayable || isUpcoming;
                        const selectEpisode = () => (isUpcoming ? openEpisodeDetails(episode) : playEpisode(episode));

                        return (
                            <EpisodeRow
                                key={`player-episode-${episode.id}`}
                                data-test-id={'player-episodes-row'}
                                aria-current={isPlaying ? 'true' : undefined}
                                isSelectable={isSelectable}
                                isUpcoming={isUpcoming}
                                role={isSelectable ? 'button' : undefined}
                                tabIndex={isSelectable ? 0 : undefined}
                                onClick={selectEpisode}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        selectEpisode();
                                    }
                                }}
                            >
                                <EpisodeThumbnail>
                                    <Picture
                                        src={episode.image ?? episode.image_url}
                                        fit={'cover'}
                                        hPosition={'center'}
                                        vPosition={'center'}
                                    />
                                    <CardOverlays
                                        progressBar={<ProgressIndicator percentage={progress < bookmarkThreshold ? progress : 0} />}
                                        contentMarker={getPlayerEpisodeContentMarker(isPlaying, requiresSubscription, isUpcoming)}
                                    />
                                </EpisodeThumbnail>
                                <EpisodeInfo>
                                    <EpisodeTitle>
                                        {requiresSubscription && (
                                            <EpisodeLockIcon
                                                data-test-id={'player-episodes-lock-icon'}
                                                svg={icons.lockIcon}
                                                width={'16px'}
                                            />
                                        )}
                                        {episode.title}
                                    </EpisodeTitle>
                                    <EpisodeDescription>{episode.description}</EpisodeDescription>
                                    {airingTime && (
                                        <EpisodeAiringTime>
                                            <span>{airingTime.day}</span>
                                            <span>{airingTime.time}</span>
                                        </EpisodeAiringTime>
                                    )}
                                </EpisodeInfo>
                            </EpisodeRow>
                        );
                    })}
                </EpisodeList>
                <EpisodeListGradient isBottom={true} isVisible={isScrolledFromBottom} aria-hidden={true} />
            </EpisodeListWrapper>
        </EpisodeSelectorContentWrapper>
    );
};

export const PlayerEpisodeSelectorMobile: FC = () => {
    const { activeControlGroup, setActiveControlGroup } = usePlayer();

    if (activeControlGroup !== ControlGroup.EPISODES) {
        return null;
    }

    const closeOverlay = () => {
        setActiveControlGroup(ControlGroup.BASE);
    };

    return (
        <EpisodeSelectorMobileWrapper>
            <EpisodeSelectorMobileCloseButton data-test-id={'player-episodes-close-button'}>
                <SVGInline svg={icons.closeIcon} onClick={closeOverlay} />
            </EpisodeSelectorMobileCloseButton>
            <EpisodeSelectorContent />
        </EpisodeSelectorMobileWrapper>
    );
};

export const PlayerEpisodeSelector: FC<{ triggerButton: ReactNode }> = ({ triggerButton }) => {
    const [wasOpened, setWasOpened] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { setActiveControlGroup } = usePlayer();

    const onVisibilityChange = (isVisible: boolean) => {
        if (isVisible) {
            setWasOpened(true);
        }

        setIsOpen(isVisible);
        setActiveControlGroup(isVisible ? ControlGroup.EPISODES : ControlGroup.BASE);
    };

    return (
        <EpisodeSelectorWrapper>
            <DropdownMenu
                triggerButton={
                    <DropdownTriggerButton
                        type="button"
                        data-popup-trigger
                        aria-label={translate('SCREEN_PLAYER_EPISODES_BUTTON')}
                        aria-expanded={isOpen}
                    >
                        {triggerButton}
                    </DropdownTriggerButton>
                }
                horizontalPosition={'right'}
                verticalPosition={'top'}
                stripMobilePortal={true}
                triggerEventName={PLAYER_EPISODES_DROPDOWN_EVENT}
                visibilityChangeCallback={onVisibilityChange}
                ignoreInsideClick
                closeOnOutsideClickOnly
            >
                {wasOpened ? <EpisodeSelectorContent /> : <EpisodeSelectorContentWrapper />}
            </DropdownMenu>
        </EpisodeSelectorWrapper>
    );
};
